import { useState, type JSX } from 'react';
import { z } from 'zod';
import {
  CAPACITY_PLANS,
  formatExactMoney,
  generateUuidV7,
  type PlanCode,
} from '@cryptology-assist/domain';
import { request, type Session } from './api';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import type { Copy } from './copy';

export function ClientInstallation({
  session,
  copy,
  onCreated,
  onCancel,
}: {
  session: Session;
  copy: Copy;
  onCreated: (botId: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [identity] = useState(() => ({
    companyId: generateUuidV7(),
    botId: generateUuidV7(),
  }));
  const [legalName, setLegalName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<PlanCode | ''>('');
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');
  const mutation = useMutation();
  return (
    <section className="settings-content">
      <h1>{copy.createClient}</h1>
      <p>{copy.createClientHelp}</p>
      <form
        onSubmit={(event) =>
          onSubmit(event, () =>
            mutation.run(async () => {
              const result = await request(
                '/v1/client-installations',
                z.object({ companyId: z.string(), botId: z.string() }),
                {
                  csrf: session.csrf,
                  body: {
                    ...identity,
                    legalName,
                    businessName,
                    name,
                    plan,
                    periodStartsAt: new Date(
                      `${starts}T00:00:00-05:00`
                    ).toISOString(),
                    periodEndsAt: new Date(
                      `${ends}T00:00:00-05:00`
                    ).toISOString(),
                  },
                }
              );
              onCreated(result.botId);
            })
          )
        }
      >
        <fieldset disabled={mutation.busy || !session.canCreateInstallation}>
          <Field label={copy.clientLegalName}>
            <input
              required
              maxLength={200}
              value={legalName}
              onChange={(event) => setLegalName(event.target.value)}
            />
          </Field>
          <Field label={copy.businessName}>
            <input
              required
              maxLength={200}
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
            />
          </Field>
          <Field label={copy.name}>
            <input
              required
              maxLength={100}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label={copy.clientPlan}>
            <select
              required
              value={plan}
              onChange={(event) => setPlan(event.target.value as PlanCode)}
            >
              <option value="">{copy.clientChoosePlan}</option>
              {Object.values(CAPACITY_PLANS).map((item) => (
                <option value={item.code} key={item.code}>
                  {item.includedConversations} ·{' '}
                  {formatExactMoney(item.monthlyPriceUsdMinor, 'USD', 2)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={copy.clientPeriodStarts}>
            <input
              required
              type="date"
              value={starts}
              onChange={(event) => setStarts(event.target.value)}
            />
          </Field>
          <Field label={copy.clientPeriodEnds}>
            <input
              required
              type="date"
              min={starts}
              value={ends}
              onChange={(event) => setEnds(event.target.value)}
            />
          </Field>
          <button className="primary">{copy.createClient}</button>
          <button type="button" onClick={onCancel}>
            {copy.cancel}
          </button>
        </fieldset>
        <ErrorNotice error={mutation.error} copy={copy} />
      </form>
    </section>
  );
}
